import React from "react";
import Page, { PageProps } from "../Page";
import DataGrid from "../../components/app/grid/DataGrid";
import { Form } from "antd";
import CreateReportingPeriodForm, {
  CreateReportingPeriodFormProps
} from "./CreateReportingPeriodForm";
import UpdateReportingPeriodForm, {
  UpdateReportingPeriodFormProps
} from "./UpdateReportingPeriodForm";
import { IReportingPeriodPresenter } from "../../../presenter/reporting/ReportingPeriodPresenter";
import ApplicantAssignationDialog from "./ApplicantAssignationDialog";
import RandomSelectionDialog from "./RandomSelectionDialog";
import { TGridAction } from "../../components/app/grid/GridActionsDropdown";
import TickBox from "../../components/app/grid/TickBox";
import Loader from "../../components/Loader";
import CreateRandomSelectionForm, {
  CreateRandomSelectionFormProps
} from "./CreateRandomSelectionForm";
import ConfirmRandomSelectionDialog from "./ConfirmRandomSelectionDialog";
import { ApplicationService } from "../../../service/ApplicationService";
import { RoleConstants } from "../../../common/Constants";

export interface Props extends PageProps {}
export interface State extends IReportingPeriodPresenter {}

export default class ReportingPeriodPage extends React.Component<Props, State> {
  private subscriptionId: number = 0;

  constructor(props: any) {
    super(props);
    this.state = { ...props };
  }

  componentDidMount() {
    this.subscriptionId = this.state.store.subscribe((result) => {
      return this.setState((prevState) => result);
    });
  }

  componentWillUnmount() {
    this.state.store.unsubscribe(this.subscriptionId);
  }

  render() {
    const {
      reportingPeriods,
      reportingTypes,
      translate,
      createReportingPeriod,
      updateReportingPeriod,
      setRowData,
      showApplicantAssignationDialog,
      hideApplicantAssignationDialog,
      applicantAssignationDialogVisible,
      assignApplicantsToReportingPeriod,
      showReportingPeriodDetails,
      onChangeRandomSelectionDialogVisible,
      randomSelectionDialogVisible,
      percetageOfCandidates,
      selectForAdditionalCheck,
      confirmRandomSelectionDialogVisible,
      onChangeConfirmModal,
      confirmData,
      randomSelectionData,
      confirmAdditionalCheck,
      canShowRandomSelection
    } = this.state;

    const { roles = [] }: { roles: string[] } = this.state as any;
    const editAllowed = ApplicationService().hasAnyRole(
      [RoleConstants.MIK_BO.MIK_ADMIN, RoleConstants.MIK_BO.MIK_OSOBLJE],
      roles
    );

    const CreateForm = Form.create<CreateReportingPeriodFormProps>({})(
      CreateReportingPeriodForm
    );

    const UpdateForm = Form.create<UpdateReportingPeriodFormProps>({})(
      UpdateReportingPeriodForm
    );

    const CreateRandomSelForm = Form.create<CreateRandomSelectionFormProps>({})(
      CreateRandomSelectionForm
    );

    const randomSelectionAction: TGridAction = {
      label: translate("random_selection.action"),
      onClick: (record) => (e) =>
        onChangeRandomSelectionDialogVisible(true, record),
      type: "default"
    };

    const assignApplicantAction: TGridAction = {
      label: translate("reporting_periods.assign_applicant"),
      onClick: (record) => (e) => {
        setRowData(record);
        showApplicantAssignationDialog();
      },
      type: "default"
    };

    const reportingPeriodDetailsAction: TGridAction = {
      label: translate("reporting_periods.details"),
      onClick: (record) => (e) => showReportingPeriodDetails(record.id),
      type: "primary"
    };

    const columns = [
      {
        title: translate("reporting_periods.reporting_type_code"),
        dataIndex: "reportingTypeCode",
        key: "reportingTypeCode",
        width: "140px",
        align: "left"
      },
      {
        title: translate("reporting_periods.name"),
        dataIndex: "name",
        key: "name"
      },
      {
        title: translate("reporting_periods.number_of_reporting_period"),
        dataIndex: "numberOfReportingPeriod",
        key: "numberOfReportingPeriod",
        width: "100px"
      },
      {
        title: translate("reporting_periods.status"),
        dataIndex: "status",
        key: "status",
        width: "110px"
      },
      {
        title: translate("reporting_periods.period_from"),
        dataIndex: "periodFrom",
        key: "periodFrom",
        width: "130px",
        sorter: (a, b) =>
          DataGrid.SortHelper.compareDateString(a.periodFrom, b.periodFrom)
      },
      {
        title: translate("reporting_periods.period_to"),
        dataIndex: "periodTo",
        key: "periodTo",
        width: "130px",
        sorter: (a, b) =>
          DataGrid.SortHelper.compareDateString(a.periodTo, b.periodTo)
      },
      {
        title: translate("reporting_periods.valid"),
        dataIndex: "valid",
        key: "valid",
        render: (text, record) => <TickBox value={record.valid} />,
        width: "5%"
      }
    ];

    let tableActions: TGridAction[] = [reportingPeriodDetailsAction];

    if (editAllowed) {
      tableActions = [assignApplicantAction, reportingPeriodDetailsAction];
      const showRandomSelection = canShowRandomSelection();
      if (showRandomSelection) {
        tableActions.push(randomSelectionAction);
      }
    }

    const dataGrid = (
      <DataGrid
        actions={tableActions}
        title={translate("grids.reporting-periods")}
        translate={translate}
        columns={columns}
        dataSource={reportingPeriods}
        allowCreate={editAllowed}
        allowUpdate={editAllowed}
        addButtonText={translate("reporting_periods.add_button_text")}
        createForm={CreateForm}
        onCreate={createReportingPeriod}
        createModalTitle={translate("reporting_periods.insert_modal_title")}
        codebooks={reportingTypes}
        updateForm={UpdateForm}
        onUpdate={updateReportingPeriod}
        updateModalTitle={translate("reporting_periods.update_modal_title")}
        booleanColumnKeys={["valid"]}
      />
    );

    return (
      <Page {...this.props}>
        <Loader
          children={dataGrid}
          translate={translate}
          loading={this.state.loaders.reportingPeriodsLoader}
        />
        <ApplicantAssignationDialog
          applicantAssignationDialogVisible={applicantAssignationDialogVisible}
          hideApplicantAssignationDialog={hideApplicantAssignationDialog}
          translate={translate}
          rowData={this.state.rowData}
          assignApplicantsToReportingPeriod={assignApplicantsToReportingPeriod}
        />
        <RandomSelectionDialog
          translate={translate}
          modalVisible={randomSelectionDialogVisible}
          onChange={onChangeRandomSelectionDialogVisible}
          Form={CreateRandomSelForm}
          onCreate={selectForAdditionalCheck}
          percentage={percetageOfCandidates}
          data={randomSelectionData}
          loading={this.state.loaders.additionalCheckLoader}
        />
        <ConfirmRandomSelectionDialog
          translate={translate}
          modalVisible={confirmRandomSelectionDialogVisible}
          onChange={onChangeConfirmModal}
          data={confirmData}
          confirm={confirmAdditionalCheck}
          loading={this.state.loaders.confirmAdditionalCheckLoader}
        />
      </Page>
    );
  }
}
